import React from 'react';

import { useIntl } from 'react-intl-next';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Badge from '@atlaskit/badge';
import { cssMap } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import Pressable from '@atlaskit/primitives/pressable';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import messages from './messages';

const styles = cssMap({
	bulkToolbarContainer: {
		position: 'fixed',
		left: 0,
		right: 0,
		bottom: token('space.100'),
		display: 'flex',
		justifyContent: 'center',
	},
	bulkToolBar: {
		alignItems: 'center',
		backgroundColor: token('color.background.neutral.bold'),
		borderRadius: token('radius.small'),
		boxShadow: token('elevation.shadow.overflow'),
		display: 'inline-flex',
		gap: token('space.150'),
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
	},
	bulkToolBarItem: {
		paddingLeft: token('space.075'),
		paddingRight: token('space.075'),
	},
	button: {
		backgroundColor: token('color.background.neutral.bold'),
		borderRadius: token('radius.small'),
		color: token('color.text.inverse'),
		height: '32px',

		'&:hover': {
			backgroundColor: token('color.background.inverse.subtle.hovered'),
		},
	},
	divider: {
		alignSelf: 'stretch',
		borderInlineStartColor: token('color.border.inverse'),
		borderInlineStartStyle: 'solid',
		borderInlineStartWidth: token('border.width'),
		height: 'auto',
	},
});

type BulkActionToolbarProps = {
	numSelected: number;
	onClearAll: (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
};

export const BulkActionToolbar = ({
	children,
	numSelected,
	onClearAll,
}: React.PropsWithChildren<BulkActionToolbarProps>): React.JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<Box xcss={styles.bulkToolbarContainer}>
			<Box xcss={styles.bulkToolBar}>
				<ClearAllButton onClick={onClearAll} />
				<Inline alignBlock="center" space="space.100" xcss={styles.bulkToolBarItem}>
					<Badge testId="bulk-action-toolbar-count-badge">{numSelected}</Badge>
					<Text color="color.text.inverse">{formatMessage(messages.selectedText)}</Text>
				</Inline>
				<Box xcss={styles.divider} />
				{children}
			</Box>
		</Box>
	);
};

type BulkActionToolbarButtonProps = {
	icon: React.ReactNode;
	onClick: (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
	text?: string;
};

export const BulkActionToolbarButton = ({
	icon,
	onClick,
	text,
}: BulkActionToolbarButtonProps): React.JSX.Element => {
	return (
		<Pressable onClick={onClick} xcss={styles.button}>
			<Inline space="space.100" xcss={styles.bulkToolBarItem}>
				{icon}
				{text}
			</Inline>
		</Pressable>
	);
};

type ClearAllButtonProps = {
	onClick: (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
};

const ClearAllButton = ({ onClick }: ClearAllButtonProps) => {
	const { formatMessage } = useIntl();
	const label = formatMessage(messages.clearAllButtonTooltip);
	return (
		<Tooltip content={label} position="top">
			<BulkActionToolbarButton icon={<CrossIcon label={label} />} onClick={onClick} />
		</Tooltip>
	);
};
